import { useState } from "react";
import { useNavigate, Link } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@features/auth";
import { useProfile } from "@/features/user-profile";
import {
	getStoredProfileAvatarUrl,
	resolveProfileAvatarUrl,
} from "@/features/user-profile/utils/avatar";
import { isPaidPlan, useSubscription, subscriptionApi } from "@/features/subscriptions";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Avatar from "@/components/ui/Avatar";
import { Globe, Moon, Sun } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { safeSetItem } from "@/utils/safeStorage";

export default function UserDropdown() {
	const { t } = useTranslation(["common", "profile"]);
	const [isOpen, setIsOpen] = useState(false);
	const [isOpeningPortal, setIsOpeningPortal] = useState(false);
	const navigate = useNavigate();
	const { user, signOut } = useAuth();
	const { data: profile } = useProfile();
	const { data: subscriptionResponse } = useSubscription();
	const subscription = subscriptionResponse?.suscripcion;
	const { i18n } = useTranslation();
	const { theme, toggleTheme } = useTheme();

	const hasPaidPlan = isPaidPlan(subscription?.plan);
	const cachedAvatarUrl = getStoredProfileAvatarUrl();
	const avatarSrc = resolveProfileAvatarUrl(
		profile?.avatar_url ?? cachedAvatarUrl,
		user?.user_metadata?.avatar_url,
	);

	const toggleLanguage = () => {
		const currentLang = i18n.language || "es";
		const newLang = currentLang.startsWith("en") ? "es" : "en";
		i18n.changeLanguage(newLang);
		safeSetItem("i18nextLng", newLang);
	};

	const handleSignOut = async () => {
		await signOut();
		toast.success(t("profile:logout"));
		navigate("/signin");
	};

	const handleManageSubscription = async () => {
		setIsOpen(false);
		try {
			setIsOpeningPortal(true);
			const proveedor = subscription?.proveedor ?? "paddle";
			const result = await subscriptionApi.getPortalUrl(proveedor);
			if (result.url) {
				window.location.href = result.url;
				return;
			}
			navigate("/settings?tab=subscription");
			toast.info(result.mensaje || "No se encontró URL de gestión.");
		} catch (error) {
			console.error("Error opening subscription portal", error);
			navigate("/settings?tab=subscription");
			toast.error("No se pudo abrir el portal de suscripción.");
		} finally {
			setIsOpeningPortal(false);
		}
	};

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
			<DropdownMenuTrigger asChild>
				<button className="dropdown-toggle group flex items-center text-gray-700 outline-none dark:text-gray-400">
					{!user ? (
						// Show only username when loading
						<span className="block text-theme-sm font-medium">Loading...</span>
					) : (
						// Show image and arrow when loaded
						<>
							<Avatar
								src={avatarSrc}
								name={user?.user_metadata?.full_name || user?.email}
								size="md"
								className="mr-3"
							/>
							<svg
								className={`stroke-gray-500 transition-transform duration-200 dark:stroke-gray-400 ${
									isOpen ? "rotate-180" : ""
								}`}
								width="18"
								height="20"
								viewBox="0 0 18 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M4.3125 8.65625L9 13.3437L13.6875 8.65625"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</>
					)}
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				className="w-[260px] border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
			>
				<div className="px-2 pb-2">
					<span className="block text-theme-sm font-medium text-gray-700 dark:text-gray-400">
						{user?.user_metadata?.full_name ||
							user?.email?.split("@")[0] ||
							"User"}
					</span>
					<span className="mt-0.5 block text-theme-xs text-gray-500 dark:text-gray-400">
						{user?.email || "user@example.com"}
					</span>
				</div>

				{hasPaidPlan ? (
					<button
						type="button"
						onClick={handleManageSubscription}
						className="mb-2 flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
					>
						{isOpeningPortal
							? "Abriendo portal..."
							: t("profile:manageSubscription")}
					</button>
				) : (
					<button
						onClick={() => { setIsOpen(false); navigate("/pricing"); }}
						className="mb-2 flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
					>
						{t("profile:upgradeToPro")}
					</button>
				)}

				<DropdownMenuItem asChild className="my-1 p-0 focus:bg-transparent">
					<Link
						to="/profile"
						onClick={() => setIsOpen(false)}
						className="group flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-theme-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
					>
						<svg
							className="fill-gray-500 group-hover:fill-gray-700 dark:fill-gray-400 dark:group-hover:fill-gray-300"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 14.1526 4.3002 16.1184 5.61936 17.616C6.17279 15.3096 8.24852 13.5955 10.7246 13.5955H13.2746C15.7509 13.5955 17.8268 15.31 18.38 17.6167C19.6996 16.119 20.5 14.153 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5ZM17.0246 18.8566V18.8455C17.0246 16.7744 15.3457 15.0955 13.2746 15.0955H10.7246C8.65354 15.0955 6.97461 16.7744 6.97461 18.8455V18.856C8.38223 19.8895 10.1198 20.5 12 20.5C13.8798 20.5 15.6171 19.8898 17.0246 18.8566ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11.9991 7.25C10.8847 7.25 9.98126 8.15342 9.98126 9.26784C9.98126 10.3823 10.8847 11.2857 11.9991 11.2857C13.1135 11.2857 14.0169 10.3823 14.0169 9.26784C14.0169 8.15342 13.1135 7.25 11.9991 7.25ZM8.48126 9.26784C8.48126 7.32499 10.0563 5.75 11.9991 5.75C13.9419 5.75 15.5169 7.32499 15.5169 9.26784C15.5169 11.2107 13.9419 12.7857 11.9991 12.7857C10.0563 12.7857 8.48126 11.2107 8.48126 9.26784Z"
								fill=""
							/>
						</svg>
						{t("profile:title")}
					</Link>
				</DropdownMenuItem>

				<DropdownMenuItem asChild className="my-1 p-0 focus:bg-transparent">
					<a
						href="https://chat.whatsapp.com/CK7Gd7BzkluGNZRWz8RVmd"
						target="_blank"
						rel="noopener noreferrer"
						onClick={() => setIsOpen(false)}
						className="group flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-theme-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
					>
						<svg
							className="fill-green-500 group-hover:fill-green-600 dark:fill-green-400 dark:group-hover:fill-green-300"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M12 2C6.477 2 2 6.477 2 12c0 1.891.525 3.66 1.438 5.168L2.05 21.95c-.066.226.161.411.36.315l4.782-1.387A9.954 9.954 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2Zm0 1.5c4.694 0 8.5 3.806 8.5 8.5s-3.806 8.5-8.5 8.5c-1.72 0-3.32-.51-4.66-1.386a.5.5 0 0 0-.441-.038l-3.23.936.936-3.23a.5.5 0 0 0-.038-.441A8.46 8.46 0 0 1 3.5 12c0-4.694 3.806-8.5 8.5-8.5Z"
								fill=""
							/>
							<path
								d="M17.447 15.11a.98.98 0 0 1-.293.707c-.45.45-1.28.65-2.09.28-.42-.19-1.02-.42-1.76-.84-.96-.55-1.8-1.25-2.43-2-.6-.73-1.03-1.54-1.29-2.38-.2-.68-.13-1.33.22-1.83.18-.25.46-.43.76-.5.16-.04.33-.06.49-.06.28 0 .54.12.72.33.24.28.51.7.75 1.14.07.13.11.27.11.41 0 .26-.14.5-.37.66-.18.13-.37.25-.55.39-.12.09-.16.25-.09.39.26.54.63 1.02 1.08 1.41s1.01.68 1.6 0c.13-.15.31-.22.5-.22.1 0 .19.02.28.06.39.18.79.43 1.15.7.25.19.38.48.38.78 0 .11-.02.22-.05.33z"
								fill=""
							/>
						</svg>
						{t("profile:community")}
					</a>
				</DropdownMenuItem>

				<DropdownMenuSeparator className="bg-gray-300 dark:bg-gray-700" />

				<DropdownMenuItem className="my-1 p-0 focus:bg-transparent">
					<button
						onClick={toggleLanguage}
						className="group flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-theme-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
					>
						<Globe className="size-5 text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300" />
						{i18n.language.startsWith("en") ? t("common:actions.spanish") : t("common:actions.english")}
					</button>
				</DropdownMenuItem>

				<DropdownMenuItem className="my-1 p-0 focus:bg-transparent">
					<button
						onClick={toggleTheme}
						className="group flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-theme-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
					>
						{theme === "dark" ? (
							<Sun className="size-5 text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300" />
						) : (
							<Moon className="size-5 text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300" />
						)}
						{theme === "dark" ? t("common:actions.lightMode") : t("common:actions.darkMode")}
					</button>
				</DropdownMenuItem>

				<DropdownMenuSeparator className="bg-gray-300 dark:bg-gray-700" />

				<DropdownMenuItem className="my-1 p-0 focus:bg-transparent">
					<button
						onClick={handleSignOut}
						className="group flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-theme-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
					>
						<svg
							className="fill-gray-500 group-hover:fill-gray-700 dark:group-hover:fill-gray-300"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M15.1007 19.247C14.6865 19.247 14.3507 18.9112 14.3507 18.497L14.3507 14.245H12.8507V18.497C12.8507 19.7396 13.8581 20.747 15.1007 20.747H18.5007C19.7434 20.747 20.7507 19.7396 20.7507 18.497L20.7507 5.49609C20.7507 4.25345 19.7433 3.24609 18.5007 3.24609H15.1007C13.8581 3.24609 12.8507 4.25345 12.8507 5.49609V9.74501L14.3507 9.74501V5.49609C14.3507 5.08188 14.6865 4.74609 15.1007 4.74609L18.5007 4.74609C18.9149 4.74609 19.2507 5.08188 19.2507 5.49609L19.2507 18.497C19.2507 18.9112 18.9149 19.247 18.5007 19.247H15.1007ZM3.25073 11.9984C3.25073 12.2144 3.34204 12.4091 3.48817 12.546L8.09483 17.1556C8.38763 17.4485 8.86251 17.4487 9.15549 17.1559C9.44848 16.8631 9.44863 16.3882 9.15583 16.0952L5.81116 12.7484L16.0007 12.7484C16.4149 12.7484 16.7507 12.4127 16.7507 11.9984C16.7507 11.5842 16.4149 11.2484 16.0007 11.2484L5.81528 11.2484L9.15585 7.90554C9.44864 7.61255 9.44847 7.13767 9.15547 6.84488C8.86248 6.55209 8.3876 6.55226 8.09481 6.84525L3.52309 11.4202C3.35673 11.5577 3.25073 11.7657 3.25073 11.9984Z"
								fill=""
							/>
						</svg>
						{t("profile:logout")}
					</button>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
